import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, HStack, Heading, VStack, Text, Show} from 'platform/foundation';
import {array, boolean, object} from 'yup';

import {DeepPartial, UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {
  PutBranchV2ApiArg,
  useGetBranchV2Query,
  useGetTenantV2Query,
  usePostBranchV2Mutation,
  usePutBranchV2Mutation,
} from '@dms/api/base';
import {AddrDetailApiResponse} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {AddressSearch, handleApiError, usePhoneNumbers, useTenant} from '@dms/shared';

import {ApiError, useNavigate, yupString} from 'shared/web';

import {BranchBillingInformationField} from './BranchBillingInformationField';
import {ContactPersonList} from './ContactPersonList';

const schema = object({
  name: yupString.required(),
  accountingExternalId: yupString,
  billingInformationId: yupString.required(),
  publicContacts: object({
    phoneNumber: object().default(null).nullable(),
    emailAddress: yupString.default(null),
    website: yupString.default(null),
  })
    .default(null)
    .nullable(),
  address: object({
    city: yupString.required(),
    street: yupString.required(),
    zip: yupString.required(),
    state: yupString,
    descriptiveNumber: yupString,
    orientationNumber: yupString,
    district: yupString,
  }).required(),
  contactPersons: array(
    object({
      isPrimary: boolean(),
      firstName: yupString,
      lastName: yupString,
      role: yupString,
      phoneNumber: object().nullable(),
      emailAddress: yupString,
    })
  )
    .default([])
    .nullable(),
});

export interface BranchFormProps {
  branchId: string | undefined;
  onDeleteBranch?: () => void;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function BranchForm(props: BranchFormProps) {
  const navigate = useNavigate();
  const isCreating = !props.branchId;

  const {data: tenant} = useGetTenantV2Query();
  const {countriesOptions} = usePhoneNumbers();
  const {tenantCountry} = useTenant();

  const [updateBranch] = usePutBranchV2Mutation();
  const [createBranch] = usePostBranchV2Mutation();

  const {
    data: branch,
    isLoading: isBranchLoading,
    isError: isBranchError,
  } = useGetBranchV2Query({id: props.branchId ?? ''}, {skip: isCreating});

  const handleSubmit: FormSubmitHandler<PutBranchV2ApiArg> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const request = isCreating
      ? createBranch(values)
      : updateBranch({...values, id: props.branchId ?? ''});

    await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.branches);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const setAddressFormSearch =
    (formApi: UseFormReturn<PutBranchV2ApiArg>) => (address: AddrDetailApiResponse) => {
      if (isNil(tenant)) {
        showNotification.error();
        return;
      }

      formApi.setValue(
        'address',
        {
          city: address?.city ?? '',
          street: address?.street ?? '',
          zip: address?.zipCode ?? '',
          state: address?.state ?? '',
          prefix: address?.prefix ?? '',
          descriptiveNumber: address?.descriptiveNumber ?? null,
          orientationNumber: address?.orientationNumber ?? null,
          addressComplement: null,
          district: null,
          country: tenant.contactInformation.address.country,
          coordinates: null,
          postal: null,
        },
        {shouldValidate: true}
      );
    };

  const defaultValues: DeepPartial<PutBranchV2ApiArg> = {
    name: branch?.name,
    accountingExternalId: branch?.accountingExternalId,
    externalId: branch?.externalId ?? null,
    publicContacts: branch?.publicContacts,
    contactPersons: branch?.contactPersons,
    address: {
      city: branch?.address?.city ?? '',
      street: branch?.address?.street ?? '',
      zip: branch?.address?.zip ?? '',
      state: branch?.address?.state ?? '',
      prefix: branch?.address?.prefix ?? '',
      descriptiveNumber: branch?.address?.descriptiveNumber ?? '',
      orientationNumber: branch?.address?.orientationNumber ?? null,
      addressComplement: branch?.address?.addressComplement ?? null,
      district: branch?.address?.district ?? null,
      country: tenantCountry ?? '',
      coordinates: null,
      postal: null,
    },
    billingInformationId: branch?.billingInformation?.id,
  };

  return (
    <DataStatus isLoading={isBranchLoading} isError={isBranchError} minHeight={60}>
      <SettingsSection>
        <Form<PutBranchV2ApiArg>
          defaultValues={defaultValues}
          schema={schema}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <VStack spacing={6}>
              <Show when={isFeatureEnabled(featureFlags.CORE_BRANCH_OPENING_TIMES) && !isCreating}>
                <VStack spacing={6}>
                  <HStack justify="space-between" align="center">
                    <Text
                      size="small"
                      color="secondary"
                    >{`${i18n.t('entity.branch.labels.branchId')}: ${props.branchId}`}</Text>

                    <Button
                      title={i18n.t('general.actions.delete')}
                      variant="danger"
                      leftIcon="action/delete"
                      onClick={props.onDeleteBranch}
                    />
                  </HStack>
                  <Separator spacing={0} />
                </VStack>
              </Show>

              <VStack spacing={5}>
                <FormField
                  control={control}
                  type="text"
                  name="name"
                  isRequired
                  label={i18n.t('page.settings.labels.marketingName')}
                  data-testid={testIds.settings.branchesEdit('marketingName')}
                />

                <FormField
                  control={control}
                  type="text"
                  name="accountingExternalId"
                  label={i18n.t('entity.branch.labels.accountingExternalId')}
                  data-testid={testIds.settings.branchesEdit('accountingExternalId')}
                />
                <FormField
                  control={control}
                  type="text"
                  name="externalId"
                  label={i18n.t('entity.vehicle.labels.externalId')}
                  data-testid={testIds.settings.branchesEdit('externalId')}
                />
              </VStack>

              <Separator spacing={5} />

              <Grid columns={2}>
                <GridItem span={2}>
                  <Heading size={4}>{i18n.t('entity.customer.labels.publicContacts')}</Heading>
                </GridItem>

                <FormField
                  name="publicContacts.phoneNumber"
                  label={i18n.t('entity.person.labels.phoneNumber')}
                  control={control}
                  type="phone"
                  countries={countriesOptions}
                  data-testid={testIds.settings.branchesEdit('publicContactsPhoneNumber')}
                />
                <FormField
                  name="publicContacts.emailAddress"
                  label={i18n.t('entity.email.labels.email')}
                  control={control}
                  type="email"
                  data-testid={testIds.settings.branchesEdit('publicContactsEmail')}
                />
                <GridItem span={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="publicContacts.website"
                    label={i18n.t('general.labels.website')}
                    data-testid={testIds.settings.branchesEdit('publicContactsWebsite')}
                  />
                </GridItem>
              </Grid>

              <Separator spacing={5} />

              <VStack spacing={5}>
                <Heading size={4}>{i18n.t('entity.address.labels.address')}</Heading>
                <AddressSearch onSelect={setAddressFormSearch(formApi)} />
                <Grid columns={4}>
                  <GridItem span={2}>
                    <FormField
                      name="address.street"
                      label={i18n.t('entity.address.labels.street')}
                      control={control}
                      type="text"
                      isRequired
                      data-testid={testIds.settings.branchesEdit('addressStreet')}
                    />
                  </GridItem>
                  <FormField
                    name="address.descriptiveNumber"
                    label={i18n.t('entity.address.labels.descriptiveNumber')}
                    control={control}
                    type="text"
                    data-testid={testIds.settings.branchesEdit('addressDescriptiveNumber')}
                  />
                  <FormField
                    name="address.orientationNumber"
                    label={i18n.t('entity.address.labels.orientationNumber')}
                    control={control}
                    type="text"
                    data-testid={testIds.settings.branchesEdit('addressOrientationNumber')}
                  />
                  <GridItem span={2}>
                    <FormField
                      name="address.city"
                      label={i18n.t('entity.address.labels.city')}
                      control={control}
                      type="text"
                      isRequired
                      data-testid={testIds.settings.branchesEdit('addressCity')}
                    />
                  </GridItem>

                  <FormField
                    name="address.district"
                    label={i18n.t('entity.address.labels.district')}
                    control={control}
                    type="text"
                    data-testid={testIds.settings.branchesEdit('addressDistrict')}
                  />
                  <FormField
                    name="address.zip"
                    label={i18n.t('entity.address.labels.zipCode')}
                    control={control}
                    type="text"
                    isRequired
                    data-testid={testIds.settings.branchesEdit('addressZipCode')}
                  />
                </Grid>
              </VStack>

              <Separator spacing={5} />

              <BranchBillingInformationField control={control} />

              <Separator spacing={5} />

              <ContactPersonList control={control} formApi={formApi} />

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.close'),
                    variant: 'secondary',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.branches),
                    'data-testid': testIds.settings.branchesEdit('close'),
                  },
                  {
                    type: 'form-button',
                    control,
                    title: i18n.t('general.actions.save'),
                    buttonType: 'submit',
                    'data-testid': testIds.settings.branchesEdit('save'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsSection>
    </DataStatus>
  );
}
